import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import { CanvasContext } from "../../../contexts/CanvasContext";
import ButtonComponent from "./ButtonComponent";
import ImageComponent from "./ImageComponent";
import HeaderComponent from "./HeaderComponent";
import ParagraphComponent from "./ParagraphComponent";
import IconComponent from "./IconComponent";
import LinkComponent from "./LinkComponent";
import CheckboxComponent from "./CheckboxComponent";
import ListComponent from "./ListComponent";
import RadioComponent from "./RadioComponent";
import TableComponent from "./TableComponent";
import TextInputComponent from "./TextComponent";
import VideoComponent from "./VideoComponent";
import RangeComponent from "./RangeComponent";
import PasswordInputComponent from "./PasswordInputComponent";
import EmailInputComponent from "./EmailInputComponent";
import NumberInputComponent from "./NumberInputComponent";
import DateInputComponent from "./DateInputComponent";
import ColorInputComponent from "./ColorInputComponent";
import FileInputComponent from "./FileInputComponent";
import AudioComponent from "./AudioComponent";
import DropDownComponent from "./DropDownComponent";
import MapComponent from "./MapComponent";
import Nav from "./Nav";
import HeaderTag from "./HeaderTag";
import Hgroup from "./Hgroup";
import Footer from "./Footer";
import Section from "./Section";
import Span from "./Span";
import FormComponent from "./FormComponent";
import Division from "./Division";
import MainComponent from "./MainComponent";
import ArticleComponent from "./ArticleComponent";
import BRTag from "./BRTag";
import HRTag from "./HRTag";
import OutputComponent from "./OutputComponent";
import IframeComponent from "./IframeComponent";

const AsideComponent = ({ aside }) => {
  const { addDivision, hoveredDivision, addElement, addCanvasDrop, files } =
    useContext(CanvasContext);
  const asideRef = useRef(null);
  const isHovered = aside.id === hoveredDivision; 
  const [isAsideHovered, setIsAsideHovered] = useState(false);
  const [isAsideFocused, setIsAsideFocused] = useState(false);
  const [isAsideActive, setIsAsideActive] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);

  const [, drop] = useDrop({
    accept: [
      "DroppableDiv",
      "DroppableButton",
      "DroppableImage",
      "DroppableHeading",
      "DroppableParagraph",
      "DroppableIcon",
      "DroppableLink",
      "DroppableCheckbox",
      "DroppableRadio",
      "DroppableList",
      "DroppableTable",
      "DroppableVideo",
      "DroppableAudio",
      "DroppableTextInput",
      "DroppableRangeInput",
      "DroppablePasswordInput",
      "DroppableEmailInput",
      "DroppableNumberInput",
      "DroppableDateInput",
      "DroppableColorInput",
      "DroppableFileInput",
      "DroppableDropDown",
      "DroppableMap",
      "DroppableNav",
      "DroppableHeader",
      "DroppableHGroup",
      "DroppableFooter",
      "DroppableSection",
      "DroppableSpan",
      "DroppableForm",
      "DroppableMain",
      "DroppableArticle",
      "DroppableAside",
      "DroppableHorizontalRule",
      "DroppableLineBreak",
      "DroppableIframe",
    ],
    drop: (item, monitor) => {
      if (!monitor.didDrop()) {
        if (item.type === "DroppableDiv") {
          addDivision(aside.id, item.name, item.type);
        } else if (
          item.type === "DroppableNav" ||
          item.type === "DroppableHeader" ||
          item.type === "DroppableHGroup" ||
          item.type === "DroppableFooter" ||
          item.type === "DroppableSection" ||
          item.type === "DroppableSpan" ||
          item.type === "DroppableForm" ||
          item.type === "DroppableMain" ||
          item.type === "DroppableArticle" ||
          item.type === "DroppableAside"
        ) {
          addCanvasDrop(aside.id, item.name, item.type);
        } else {
          addElement(aside.id, item.name, item.type);
        }
      }
    },
  });

  const constructGradientString = () => {
    const {
      backgroundGradientImage,
      gradientDirection,
      gradientShape,
      gradientPosition,
      startColor,
      endColor,
      gradientAngle,
      patternLength,
    } = aside;

    switch (backgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${gradientDirection || "to right"}, ${
          startColor || "#ff7e5f"
        }, ${endColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${gradientAngle || "45"}deg, ${
          startColor || "#606dbc"
        }, ${endColor || "#465298"} ${patternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"} ${
          patternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };
  const constructHoverGradientString = () => {
    const {
      hoveredbackgroundGradientImage,
      hoveredgradientDirection,
      hoveredgradientShape,
      hoveredgradientPosition,
      hoveredstartColor,
      hoveredendColor,
      hoveredgradientAngle,
      hoveredpatternLength,
    } = aside;

    switch (hoveredbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${hoveredgradientDirection || "to right"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${hoveredgradientShape || "circle"} at ${
          hoveredgradientPosition || "center"
        }, ${hoveredstartColor || "#ff7e5f"}, ${hoveredendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${hoveredgradientAngle || "45"}deg, ${
          hoveredstartColor || "#606dbc"
        }, ${hoveredendColor || "#465298"} ${hoveredpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          hoveredgradientShape || "circle"
        } at ${hoveredgradientPosition || "center"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"} ${hoveredpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };
  const constructFocusedGradientString = () => {
    const {
      focusedbackgroundGradientImage,
      focusedgradientDirection,
      focusedgradientShape,
      focusedgradientPosition,
      focusedstartColor,
      focusedendColor,
      focusedgradientAngle,
      focusedpatternLength,
    } = aside;

    switch (focusedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${focusedgradientDirection || "to right"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${focusedgradientShape || "circle"} at ${
          focusedgradientPosition || "center"
        }, ${focusedstartColor || "#ff7e5f"}, ${focusedendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${focusedgradientAngle || "45"}deg, ${
          focusedstartColor || "#606dbc"
        }, ${focusedendColor || "#465298"} ${focusedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          focusedgradientShape || "circle"
        } at ${focusedgradientPosition || "center"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"} ${focusedpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };
  const constructActivatedGradientString = () => {
    const {
      activatedbackgroundGradientImage,
      activatedgradientDirection,
      activatedgradientShape,
      activatedgradientPosition,
      activatedstartColor,
      activatedendColor,
      activatedgradientAngle,
      activatedpatternLength,
    } = aside;

    switch (activatedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${activatedgradientDirection || "to right"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${activatedgradientShape || "circle"} at ${
          activatedgradientPosition || "center"
        }, ${activatedstartColor || "#ff7e5f"}, ${
          activatedendColor || "#feb47b"
        })`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${
          activatedgradientAngle || "45"
        }deg, ${activatedstartColor || "#606dbc"}, ${
          activatedendColor || "#465298"
        } ${activatedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          activatedgradientShape || "circle"
        } at ${activatedgradientPosition || "center"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"} ${
          activatedpatternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };

  const generateKeyframes = () => {
    if (!aside.keyframes || aside.keyframes.length === 0) return null;

    return aside.keyframes.map((kf) => {
      const properties = {};
      let transform = "";

      Object.entries(kf.properties).forEach(([key, value]) => {
        // Remove 'KEY' prefix if present
        let processedKey = key.startsWith("KEY") ? key.slice(3) : key;

        // Handle transform properties
        if (
          processedKey === "scale" ||
          processedKey === "translateX" ||
          processedKey === "translateY" ||
          processedKey === "rotate" ||
          processedKey === "skewX" ||
          processedKey === "skewY"
        ) {
          if (value) {
            switch (processedKey) {
              case "scale":
                transform += `scale(${value}) `;
                break;
              case "translateX":
                transform += `translateX(${value}) `;
                break;
              case "translateY":
                transform += `translateY(${value}) `;
                break;
              case "rotate":
                transform += `rotate(${value}) `;
                break;
              case "skewX":
                transform += `skewX(${value}) `;
                break;
              case "skewY":
                transform += `skewY(${value}) `;
                break;
              default:
                break;
            }
          }
        } else {
          // Convert camelCase to kebab-case for other CSS properties
          properties[processedKey] = value;
        }
      });

      // Add transform property if it was built
      if (transform) {
        properties["transform"] = transform.trim();
      }

      return {
        offset: kf.position / 100,
        ...properties,
      };
    });
  };
  const combinedRef = useMemo(
    () => (node) => {
      asideRef.current = node;
      drop(node);
    },
    [drop]
  );
  useEffect(() => {
    if (asideRef.current && aside.keyframes && aside.keyframes.length > 0) {
      const keyframes = generateKeyframes();
      const animation = asideRef.current.animate(keyframes, {
        duration: parseFloat(aside.animationDuration) * 1000 || 1000,
        delay: parseFloat(aside.animationDelay) * 1000 || 0,
        iterations:
          aside.animationIterationCount === "infinite"
            ? Infinity
            : parseFloat(aside.animationIterationCount) || 1,
        direction: aside.animationDirection || "normal",
        fill: aside.animationFillMode || "none",
        easing: aside.animationTimingFunction || "linear",
      });

      return () => animation.cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    aside.keyframes,
    aside.animationDuration,
    aside.animationDelay,
    aside.animationIterationCount,
    aside.animationDirection,
    aside.animationFillMode,
    aside.animationTimingFunction,
  ]);
  useEffect(() => {
    const fetchBackgroundImage = async () => {
      if (aside.backgroundImage && aside.backgroundImage.startsWith("./")) {
        const imageName = aside.backgroundImage.replace("./", "");
        const file = files.find((f) => f.name === imageName);
        if (file && file.data) {
          setBackgroundImageUrl(file.data);
        } else {
          console.error("Image not found in files:", imageName);
        }
      } else {
        setBackgroundImageUrl(aside.backgroundImage);
      }
    };

    fetchBackgroundImage();
  }, [aside.backgroundImage, files]);

  const focusedStyles =
    isAsideFocused && aside.pseudoClass === "focus"
      ? {
          width: aside.focusedwidth || aside.width || "100%",
          height: aside.focusedheight || aside.height || "auto",

          padding: aside.focusedpadding || aside.padding || "8px",
          paddingTop: aside.focusedpaddingTop || aside.focusedpadding || "8px",
          paddingRight:
            aside.focusedpaddingRight || aside.focusedpadding || "8px",
          paddingBottom:
            aside.focusedpaddingBottom || aside.focusedpadding || "8px",
          paddingLeft:
            aside.focusedpaddingLeft || aside.focusedpadding || "8px",
          margin: aside.focusedmargin || aside.margin || "0px",
          marginTop: aside.focusedmarginTop || aside.focusedmargin || "0px",
          marginRight: aside.focusedmarginRight || aside.focusedmargin || "0px",
          marginBottom:
            aside.focusedmarginBottom || aside.focusedmargin || "0px",
          marginLeft: aside.focusedmarginLeft || aside.focusedmargin || "0px",

          borderTop:
            aside.focusedborderTop ||
            aside.focusedborder ||
            aside.border ||
            "1px solid black",
          borderRight:
            aside.focusedborderRight ||
            aside.focusedborder ||
            aside.border ||
            "1px solid black",
          borderBottom:
            aside.focusedborderBottom ||
            aside.focusedborder ||
            aside.border ||
            "1px solid black",
          borderLeft:
            aside.focusedborderLeft ||
            aside.focusedborder ||
            aside.border ||
            "1px solid black",
          borderSapcing:
            aside.focusedborderSpacing || aside.borderSpacing || "0px",
          borderCollapse:
            aside.focusedborderCollapse || aside.borderCollapse || "0px",
          borderRadius: aside.focusedborderRadius || aside.borderRadius || "0",
          outline: aside.focusedoutline || aside.outline || "none",

          position: aside.focusedposition || aside.position || "relative",
          top: aside.focusedtop || aside.top || "auto",
          right: aside.focusedright || aside.right || "auto",
          bottom: aside.focusedbottom || aside.bottom || "auto",
          left: aside.focusedleft || aside.left || "auto",
          zIndex: aside.focusedzIndex || aside.zIndex || "auto",
          boxShadow: aside.focusedboxShadow || aside.boxShadow || "none",
          opacity: aside.focusedOpacity || aside.opacity || 1,
          display: aside.focuseddisplay || aside.display || "block",
          flexDirection:
            aside.focusedflexDirection || aside.flexDirection || "column",
          flexWrap: aside.focusedflexWrap || "nowrap",
          order: aside.focusedorder || "0",
          flexGrow: aside.focusedflexGrow || "0",
          flexShrink: aside.focusedflexShrink || "1",
          flexBasis: aside.focusedflexBasis || "auto",

          gridTemplateColumns: aside.focusedgridTemplateColumns || "none",
          gridTemplateRows: aside.focusedgridTemplateRows || "none",
          gridArea: aside.focusedgridArea || "auto",
          gridAutoFlow: aside.focusedgridAutoFlow || "row",
          gridAutoColumns: aside.focusedgridAutoColumns || "auto",
          gridAutoRows: aside.focusedgridAutoRows || "auto",
          columnGap: aside.focusedcolumnGap || "0",
          rowGap: aside.focusedrowGap || "0",

          justifyContent: aside.focusedjustifyContent || "flex-start",
          alignItems: aside.focusedalignItems || "stretch",
          alignContent: aside.focusedalignContent || "stretch",
          alignSelf: aside.focusedalignSelf || "auto",
          // placeContent: aside.focusedplaceContent || "normal",
          // placeItems: aside.focusedplaceItems || "normal",
          justifyItems: aside.focusedjustifyItems || "stretch",
          justifySelf: aside.focusedjustifySelf || "auto",

          gap: aside.focusedgap || "0",
          visibility: aside.focusedvisibility || "visible",

          color: aside.focusedcolor || aside.color || "#000000",
          backgroundColor:
            aside.focusedbackgroundColor || aside.backgroundColor || "#ffffff",
          backgroundImage: (() => {
            if (aside.focusedbackgroundImage) {
              if (aside.focusedbackgroundImage.startsWith("./")) {
                const imageName = aside.focusedbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${aside.focusedbackgroundImage})`;
            }
            if (aside.backgroundImage) {
              if (aside.backgroundImage.startsWith("./")) {
                const imageName = aside.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${aside.backgroundImage})`;
            }
            return (
              constructFocusedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            aside.focusedbackgroundPosition ||
            aside.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            aside.focusedbackgroundSize || aside.backgroundSize || "auto",
          backgroundRepeat:
            aside.focusedbackgroundRepeat || aside.backgroundRepeat || "repeat",
          backgroundAttachment:
            aside.focusedbackgroundAttachment ||
            aside.backgroundAttachment ||
            "scroll",

          fontFamily: aside.focusedfontFamily || aside.fontFamily || "inherit",
          fontSize: aside.focusedfontSize || aside.fontSize || "inherit",
          fontWeight: aside.focusedfontWeight || aside.fontWeight || "normal",
          fontStyle: aside.focusedfontStyle || aside.fontStyle || "normal",
          fontVariant:
            aside.focusedfontVariant || aside.fontVariant || "normal",
          lineHeight: aside.focusedlineHeight || aside.lineHeight || "normal",
          textShadow: aside.focusedtextShadow || aside.textShadow || "none",
          textAlign: aside.focusedtextAlign || aside.textAlign || "left",
          verticalAlign:
            aside.focusedverticalAlign || aside.verticalAlign || "baseline",
          textDecoration:
            aside.focusedtextDecoration || aside.textDecoration || "none",
          textTransform:
            aside.focusedtextTransform || aside.textTransform || "none",
          letterSpacing:
            aside.focusedletterSpacing || aside.letterSpacing || "normal",
          wordSpacing:
            aside.focusedwordSpacing || aside.wordSpacing || "normal",
          textIndent: aside.focusedtextIndent || aside.textIndent || "0",
          whiteSpace: aside.focusedwhiteSpace || aside.whiteSpace || "normal",
          textOverflow:
            aside.focusedtextOverflow || aside.textOverflow || "clip",
          direction: aside.focuseddirection || aside.direction || "ltr",
          unicodeBidi:
            aside.focusedunicodeBidi || aside.unicodeBidi || "normal",
          writingMode:
            aside.focusedwritingMode || aside.writingMode || "horizontal-tb",

          cursor: aside.focusedcursor || aside.cursor || "default",
          filter: aside.focusedfilter || aside.filter || "none",
          backdropFilter:
            aside.focusedbackdropFilter || aside.backdropFilter || "none",
          mixBlendMode:
            aside.focusedmixBlendMode || aside.mixBlendMode || "normal",
          aspectRatio: aside.focusedaspectRatio || aside.aspectRatio || "auto",

          transform: aside.focusedtransform || aside.transform || "none",
          transition: aside.focusedtransition || aside.transition || "none",
        }
      : {};

  const hoverStyles =
    isAsideHovered && aside.pseudoClass === "hover"
      ? {
          width: aside.hoveredwidth || aside.width || "100%",
          height: aside.hoveredheight || aside.height || "auto",

          padding: aside.hoveredpadding || aside.padding || "8px",
          paddingTop: aside.hoveredpaddingTop || aside.hoveredpadding || "8px",
          paddingRight:
            aside.hoveredpaddingRight || aside.hoveredpadding || "8px",
          paddingBottom:
            aside.hoveredpaddingBottom || aside.hoveredpadding || "8px",
          paddingLeft:
            aside.hoveredpaddingLeft || aside.hoveredpadding || "8px",
          margin: aside.hoveredmargin || aside.margin || "0px",
          marginTop: aside.hoveredmarginTop || aside.hoveredmargin || "0px",
          marginRight: aside.hoveredmarginRight || aside.hoveredmargin || "0px",
          marginBottom:
            aside.hoveredmarginBottom || aside.hoveredmargin || "0px",
          marginLeft: aside.hoveredmarginLeft || aside.hoveredmargin || "0px",

          borderTop:
            aside.hoveredborderTop ||
            aside.hoveredborder ||
            aside.border ||
            "1px solid black",
          borderRight:
            aside.hoveredborderRight ||
            aside.hoveredborder ||
            aside.border ||
            "1px solid black",
          borderBottom:
            aside.hoveredborderBottom ||
            aside.hoveredborder ||
            aside.border ||
            "1px solid black",
          borderLeft:
            aside.hoveredborderLeft ||
            aside.hoveredborder ||
            aside.border ||
            "1px solid black",
          borderSapcing:
            aside.hoveredborderSpacing || aside.borderSpacing || "0px",
          borderCollapse:
            aside.hoveredborderCollapse || aside.borderCollapse || "0px",
          borderRadius: aside.hoveredborderRadius || aside.borderRadius || "0",
          outline: aside.hoveredoutline || aside.outline || "none",

          position: aside.hoveredposition || aside.position || "relative",
          top: aside.hoveredtop || aside.top || "auto",
          right: aside.hoveredright || aside.right || "auto",
          bottom: aside.hoveredbottom || aside.bottom || "auto",
          left: aside.hoveredleft || aside.left || "auto",
          zIndex: aside.hoveredzIndex || aside.zIndex || "auto",
          boxShadow: aside.hoveredboxShadow || aside.boxShadow || "none",
          opacity: aside.hoveredOpacity || aside.opacity || 1,
          display: aside.hovereddisplay || aside.display || "block",
          flexDirection:
            aside.hoveredflexDirection || aside.flexDirection || "column",
          flexWrap: aside.hoveredflexWrap || "nowrap",
          order: aside.hoveredorder || "0",
          flexGrow: aside.hoveredflexGrow || "0",
          flexShrink: aside.hoveredflexShrink || "1",
          flexBasis: aside.hoveredflexBasis || "auto",

          gridTemplateColumns: aside.hoveredgridTemplateColumns || "none",
          gridTemplateRows: aside.hoveredgridTemplateRows || "none",
          gridArea: aside.hoveredgridArea || "auto",
          gridAutoFlow: aside.hoveredgridAutoFlow || "row",
          gridAutoColumns: aside.hoveredgridAutoColumns || "auto",
          gridAutoRows: aside.hoveredgridAutoRows || "auto",
          columnGap: aside.hoveredcolumnGap || "0",
          rowGap: aside.hoveredrowGap || "0",

          justifyContent: aside.hoveredjustifyContent || "flex-start",
          alignItems: aside.hoveredalignItems || "stretch",
          alignContent: aside.hoveredalignContent || "stretch",
          alignSelf: aside.hoveredalignSelf || "auto",
          // placeContent: aside.hoveredplaceContent || "normal",
          // placeItems: aside.hoveredplaceItems || "normal",
          justifyItems: aside.hoveredjustifyItems || "stretch",
          justifySelf: aside.hoveredjustifySelf || "auto",

          gap: aside.hoveredgap || "0",
          visibility: aside.hoveredvisibility || "visible",

          color: aside.hoveredcolor || aside.color || "#000000",
          backgroundColor:
            aside.hoveredbackgroundColor || aside.backgroundColor || "#ffffff",
          backgroundImage: (() => {
            if (aside.hoveredbackgroundImage) {
              if (aside.hoveredbackgroundImage.startsWith("./")) {
                const imageName = aside.hoveredbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${aside.hoveredbackgroundImage})`;
            }
            if (aside.backgroundImage) {
              if (aside.backgroundImage.startsWith("./")) {
                const imageName = aside.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${aside.backgroundImage})`;
            }
            return (
              constructHoverGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            aside.hoveredbackgroundPosition ||
            aside.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            aside.hoveredbackgroundSize || aside.backgroundSize || "auto",
          backgroundRepeat:
            aside.hoveredbackgroundRepeat || aside.backgroundRepeat || "repeat",
          backgroundAttachment:
            aside.hoveredbackgroundAttachment ||
            aside.backgroundAttachment ||
            "scroll",

          fontFamily: aside.hoveredfontFamily || aside.fontFamily || "inherit",
          fontSize: aside.hoveredfontSize || aside.fontSize || "inherit",
          fontWeight: aside.hoveredfontWeight || aside.fontWeight || "normal",
          fontStyle: aside.hoveredfontStyle || aside.fontStyle || "normal",
          fontVariant:
            aside.hoveredfontVariant || aside.fontVariant || "normal",
          lineHeight: aside.hoveredlineHeight || aside.lineHeight || "normal",
          textShadow: aside.hoveredtextShadow || aside.textShadow || "none",
          textAlign: aside.hoveredtextAlign || aside.textAlign || "left",
          verticalAlign:
            aside.hoveredverticalAlign || aside.verticalAlign || "baseline",
          textDecoration:
            aside.hoveredtextDecoration || aside.textDecoration || "none",
          textTransform:
            aside.hoveredtextTransform || aside.textTransform || "none",
          letterSpacing:
            aside.hoveredletterSpacing || aside.letterSpacing || "normal",
          wordSpacing:
            aside.hoveredwordSpacing || aside.wordSpacing || "normal",
          textIndent: aside.hoveredtextIndent || aside.textIndent || "0",
          whiteSpace: aside.hoveredwhiteSpace || aside.whiteSpace || "normal",
          textOverflow:
            aside.hoveredtextOverflow || aside.textOverflow || "clip",
          direction: aside.hovereddirection || aside.direction || "ltr",
          unicodeBidi:
            aside.hoveredunicodeBidi || aside.unicodeBidi || "normal",
          writingMode:
            aside.hoveredwritingMode || aside.writingMode || "horizontal-tb",

          cursor: aside.hoveredcursor || aside.cursor || "default",
          filter: aside.hoveredfilter || aside.filter || "none",
          backdropFilter:
            aside.hoveredbackdropFilter || aside.backdropFilter || "none",
          mixBlendMode:
            aside.hoveredmixBlendMode || aside.mixBlendMode || "normal",
          aspectRatio: aside.hoveredaspectRatio || aside.aspectRatio || "auto",

          transform: aside.hoveredtransform || aside.transform || "none",
          transition: aside.hoveredtransition || aside.transition || "none",
        }
      : {};

  const activeStyles =
    isAsideActive && aside.pseudoClass === "active"
      ? {
          width: aside.activatedwidth || aside.width || "100%",
          height: aside.hoveredheight || aside.height || "auto",

          padding: aside.activatedpadding || aside.padding || "8px",
          paddingTop:
            aside.activatedpaddingTop || aside.activatedpadding || "8px",
          paddingRight:
            aside.activatedpaddingRight || aside.activatedpadding || "8px",
          paddingBottom:
            aside.activatedpaddingBottom || aside.activatedpadding || "8px",
          paddingLeft:
            aside.activatedpaddingLeft || aside.activatedpadding || "8px",
          margin: aside.activatedmargin || aside.margin || "0px",
          marginTop: aside.activatedmarginTop || aside.activatedmargin || "0px",
          marginRight:
            aside.activatedmarginRight || aside.activatedmargin || "0px",
          marginBottom:
            aside.activatedmarginBottom || aside.activatedmargin || "0px",
          marginLeft:
            aside.activatedmarginLeft || aside.activatedmargin || "0px",

          borderTop:
            aside.activatedborderTop ||
            aside.activatedborder ||
            aside.border ||
            "1px solid black",
          borderRight:
            aside.activatedborderRight ||
            aside.activatedborder ||
            aside.border ||
            "1px solid black",
          borderBottom:
            aside.activatedborderBottom ||
            aside.activatedborder ||
            aside.border ||
            "1px solid black",
          borderLeft:
            aside.activatedborderLeft ||
            aside.activatedborder ||
            aside.border ||
            "1px solid black",
          borderSapcing:
            aside.activatedborderSpacing || aside.borderSpacing || "0px",
          borderCollapse:
            aside.activatedborderCollapse || aside.borderCollapse || "0px",
          borderRadius:
            aside.activatedborderRadius || aside.borderRadius || "0",
          outline: aside.activatedoutline || aside.outline || "none",

          position: aside.activatedposition || aside.position || "relative",
          top: aside.activatedtop || aside.top || "auto",
          right: aside.activatedright || aside.right || "auto",
          bottom: aside.activatedbottom || aside.bottom || "auto",
          left: aside.activatedleft || aside.left || "auto",
          zIndex: aside.activatedzIndex || aside.zIndex || "auto",

          boxShadow: aside.activatedboxShadow || aside.boxShadow || "none",
          opacity: aside.activatedOpacity || aside.opacity || 1,
          display: aside.activateddisplay || aside.display || "block",
          flexDirection:
            aside.activatedflexDirection || aside.flexDirection || "column",
          flexWrap: aside.activatedflexWrap || "nowrap",
          order: aside.activatedorder || "0",
          flexGrow: aside.activatedflexGrow || "0",
          flexShrink: aside.activatedflexShrink || "1",
          flexBasis: aside.activatedflexBasis || "auto",

          gridTemplateColumns: aside.activatedgridTemplateColumns || "none",
          gridTemplateRows: aside.activatedgridTemplateRows || "none",
          gridArea: aside.activatedgridArea || "auto",
          gridAutoFlow: aside.activatedgridAutoFlow || "row",
          gridAutoColumns: aside.activatedgridAutoColumns || "auto",
          gridAutoRows: aside.activatedgridAutoRows || "auto",
          columnGap: aside.activatedcolumnGap || "0",
          rowGap: aside.activatedrowGap || "0",

          justifyContent: aside.activatedjustifyContent || "flex-start",
          alignItems: aside.activatedalignItems || "stretch",
          alignContent: aside.activatedalignContent || "stretch",
          alignSelf: aside.activatedalignSelf || "auto",
          // placeContent: aside.activatedplaceContent || "normal",
          // placeItems: aside.activatedplaceItems || "normal",
          justifyItems: aside.activatedjustifyItems || "stretch",
          justifySelf: aside.activatedjustifySelf || "auto",

          gap: aside.activatedgap || "0",
          visibility: aside.activatedvisibility || "visible",

          color: aside.activatedcolor || aside.color || "#000000",
          backgroundColor:
            aside.activatedbackgroundColor ||
            aside.backgroundColor ||
            "#ffffff",
          backgroundImage: (() => {
            if (aside.activatedbackgroundImage) {
              if (aside.activatedbackgroundImage.startsWith("./")) {
                const imageName = aside.activatedbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${aside.activatedbackgroundImage})`;
            }
            if (aside.backgroundImage) {
              if (aside.backgroundImage.startsWith("./")) {
                const imageName = aside.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${aside.backgroundImage})`;
            }
            return (
              constructActivatedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            aside.activatedbackgroundPosition ||
            aside.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            aside.activatedbackgroundSize || aside.backgroundSize || "auto",
          backgroundRepeat:
            aside.activatedbackgroundRepeat ||
            aside.backgroundRepeat ||
            "repeat",
          backgroundAttachment:
            aside.activatedbackgroundAttachment ||
            aside.backgroundAttachment ||
            "scroll",

          fontFamily:
            aside.activatedfontFamily || aside.fontFamily || "inherit",
          fontSize: aside.activatedfontSize || aside.fontSize || "inherit",
          fontWeight: aside.activatedfontWeight || aside.fontWeight || "normal",
          fontStyle: aside.activatedfontStyle || aside.fontStyle || "normal",
          fontVariant:
            aside.activatedfontVariant || aside.fontVariant || "normal",
          lineHeight: aside.activatedlineHeight || aside.lineHeight || "normal",
          textShadow: aside.activatedtextShadow || aside.textShadow || "none",
          textAlign: aside.activatedtextAlign || aside.textAlign || "left",
          verticalAlign:
            aside.activatedverticalAlign || aside.verticalAlign || "baseline",
          textDecoration:
            aside.activatedtextDecoration || aside.textDecoration || "none",
          textTransform:
            aside.activatedtextTransform || aside.textTransform || "none",
          letterSpacing:
            aside.activatedletterSpacing || aside.letterSpacing || "normal",
          wordSpacing:
            aside.activatedwordSpacing || aside.wordSpacing || "normal",
          textIndent: aside.activatedtextIndent || aside.textIndent || "0",
          whiteSpace: aside.activatedwhiteSpace || aside.whiteSpace || "normal",
          textOverflow:
            aside.activatedtextOverflow || aside.textOverflow || "clip",
          direction: aside.activateddirection || aside.direction || "ltr",
          unicodeBidi:
            aside.activatedunicodeBidi || aside.unicodeBidi || "normal",
          writingMode:
            aside.activatedwritingMode || aside.writingMode || "horizontal-tb",

          cursor: aside.activatedcursor || aside.cursor || "default",
          filter: aside.activatedfilter || aside.filter || "none",
          backdropFilter:
            aside.activatedbackdropFilter || aside.backdropFilter || "none",
          mixBlendMode:
            aside.activatedmixBlendMode || aside.mixBlendMode || "normal",
          aspectRatio:
            aside.activatedaspectRatio || aside.aspectRatio || "auto",

          transform: aside.activatedtransform || aside.transform || "none",
          transition: aside.activatedtransition || aside.transition || "none",
        }
      : {};

  const asideStyle = {
    width: aside.width || "100%",
    height: aside.height || "auto",
    padding: aside.padding || "8px",
    paddingTop: aside.paddingTop || aside.padding || "8px",
    paddingRight: aside.paddingRight || aside.padding || "8px",
    paddingBottom: aside.paddingBottom || aside.padding || "8px",
    paddingLeft: aside.paddingLeft || aside.padding || "8px",
    margin: aside.margin || "0px",
    marginTop: aside.marginTop || aside.margin || "0px",
    marginRight: aside.marginRight || aside.margin || "0px",
    marginBottom: aside.marginBottom || aside.margin || "0px",
    marginLeft: aside.marginLeft || aside.margin || "0px",

    borderTop: aside.borderTop || aside.border || "1px solid #000000",
    borderRight: aside.borderRight || aside.border || "1px solid #000000",
    borderBottom: aside.borderBottom || aside.border || "1px solid #000000",
    borderLeft: aside.borderLeft || aside.border || "1px solid #000000",

    borderSapcing: aside.borderSpacing || "0px",
    borderCollapse: aside.borderCollapse || "0px",
    borderRadius: aside.borderRadius || "0",
    boxSizing: aside.boxSizing || "border-box",

    position: aside.position || "relative",
    top: aside.top || "auto",
    right: aside.right || "auto",
    bottom: aside.bottom || "auto",
    left: aside.left || "auto",
    zIndex: aside.zIndex || "auto",

    display: aside.display || "block",
    flexDirection: aside.flexDirection || "column",
    flexWrap: aside.flexWrap || "nowrap",
    order: aside.order || "0",
    flexGrow: aside.flexGrow || "0",
    flexShrink: aside.flexShrink || "1",
    flexBasis: aside.flexBasis || "auto",

    gridTemplateColumns: aside.gridTemplateColumns || "none",
    gridTemplateRows: aside.gridTemplateRows || "none",
    gridArea: aside.gridArea || "auto",
    gridAutoFlow: aside.gridAutoFlow || "row",
    gridAutoColumns: aside.gridAutoColumns || "auto",
    gridAutoRows: aside.gridAutoRows || "auto",
    columnGap: aside.columnGap || "0",
    rowGap: aside.rowGap || "0",

    justifyContent: aside.justifyContent || "flex-start",
    alignItems: aside.alignItems || "stretch",
    alignContent: aside.alignContent || "stretch",
    alignSelf: aside.alignSelf || "auto",
    // placeContent: aside.placeContent || "normal",
    // placeItems: aside.placeItems || "normal",
    justifyItems: aside.justifyItems || "stretch",
    justifySelf: aside.justifySelf || "auto",

    gap: aside.gap || "0",
    visibility: aside.visibility || "visible",

    color: aside.color || "#000000",
    backgroundImage: backgroundImageUrl
      ? `url(${backgroundImageUrl})`
      : constructGradientString(),

    backgroundPosition: aside.backgroundPosition || "0% 0%",
    backgroundSize: aside.backgroundSize || "auto",
    backgroundRepeat: aside.backgroundRepeat || "repeat",
    backgroundAttachment: aside.backgroundAttachment || "scroll",
    boxShadow: aside.boxShadow || "none",
    filter: aside.filter || "none",
    backdropFilter: aside.backdropFilter || "none",
    mixBlendMode: aside.mixBlendMode || "normal",
    aspectRatio: aside.aspectRatio || "auto",

    resize: aside.resize || "none",
    userSelect: aside.userSelect || "auto",
    overflow: aside.resize && aside.resize !== "none" ? "auto" : "visible",
    overflowX: aside.overflowX || "hidden",
    overflowY: aside.overflowY || "hidden",

    fontSize: aside.fontSize || "inherit",
    fontFamily: aside.fontFamily || "inherit",
    fontWeight: aside.fontWeight || "normal",
    fontStyle: aside.fontStyle || "normal",
    fontVariant: aside.fontVariant || "normal",
    lineHeight: aside.lineHeight || "normal",
    textAlign: aside.textAlign || "left",
    textDecoration: aside.textDecoration || "none",
    textTransform: aside.textTransform || "none",
    textShadow: aside.textShadow || "none",
    verticalAlign: aside.verticalAlign || "baseline",
    letterSpacing: aside.letterSpacing || "normal",
    wordSpacing: aside.wordSpacing || "normal",
    textIndent: aside.textIndent || "0",
    whiteSpace: aside.whiteSpace || "normal",
    textOverflow: aside.textOverflow || "clip",
    direction: aside.direction || "ltr",
    unicodeBidi: aside.unicodeBidi || "normal",
    writingMode: aside.writingMode || "horizontal-tb",
    cursor: aside.cursor || "default",
    outline: aside.outline || "none",

    backgroundColor: isHovered ? "#ADD8E6" : aside.backgroundColor || "#ffffff",

    listStyleType: aside.listStyleType || "disc",
    listStyleImage: aside.listStyleImage
      ? `url(${aside.listStyleImage})`
      : "none",
    listStylePosition: aside.listStylePosition || "outside",

    transform: aside.transform || "none",
    transition:
      aside.transition || "transform 0.3s ease, background-color 0.3s ease",

    ...hoverStyles,
    ...focusedStyles,
    ...activeStyles,
  };

  return (
    <div
      className="Main_Division"
      ref={combinedRef}
      style={asideStyle}
      onMouseEnter={() => setIsAsideHovered(true)}
      onMouseLeave={() => setIsAsideHovered(false)}
      onFocus={() => setIsAsideFocused(true)}
      onBlur={() => setIsAsideFocused(false)}
      onMouseDown={() => setIsAsideActive(true)}
      onMouseUp={() => setIsAsideActive(false)}
      tabIndex={0}
    >
      {aside.children &&
        aside.children.map((child) => {
          switch (child.name) {
            case "Button":
              return <ButtonComponent key={child.id} button={child} />;
            case "Image":
              return <ImageComponent key={child.id} image={child} />;
            case "Heading":
              return <HeaderComponent key={child.id} header={child} />;
            case "Paragraph":
              return <ParagraphComponent key={child.id} paragraph={child} />;
            case "Icon":
              return <IconComponent key={child.id} icon={child} />;
            case "Link":
              return <LinkComponent key={child.id} link={child} />;
            case "Checkbox":
              return <CheckboxComponent key={child.id} checkbox={child} />;
            case "Radio":
              return <RadioComponent key={child.id} radio={child} />;
            case "List":
              return <ListComponent key={child.id} list={child} />;
            case "Table":
              return <TableComponent key={child.id} table={child} />;
            case "Video":
              return <VideoComponent key={child.id} video={child} />;
            case "Text":
              return <TextInputComponent key={child.id} textInput={child} />;
            case "Range":
              return <RangeComponent key={child.id} range={child} />;
            case "Password":
              return (
                <PasswordInputComponent key={child.id} passwordInput={child} />
              );
            case "Email":
              return <EmailInputComponent key={child.id} emailInput={child} />;
            case "Number":
              return (
                <NumberInputComponent key={child.id} numberInput={child} />
              );
            case "Date":
              return <DateInputComponent key={child.id} dateInput={child} />;
            case "Color":
              return <ColorInputComponent key={child.id} colorInput={child} />;
            case "File":
              return <FileInputComponent key={child.id} fileInput={child} />;
            case "Audio":
              return <AudioComponent key={child.id} audio={child} />;
            case "DropDown":
              return <DropDownComponent key={child.id} dropdown={child} />;
            case "Output":
              return <OutputComponent key={child.id} output={child} />;
            case "Map":
              return <MapComponent key={child.id} map={child} />;
            case "Nav":
              return <Nav key={child.id} nav={child} />;
            case "Header":
              return <HeaderTag key={child.id} headertag={child} />;
            case "HGroup":
              return <Hgroup key={child.id} hgroup={child} />;
            case "Footer":
              return <Footer key={child.id} footer={child} />;
            case "Section":
              return <Section key={child.id} section={child} />;
            case "Span":
              return <Span key={child.id} span={child} />;
            case "Form":
              return <FormComponent key={child.id} form={child} />;
            case "Main":
              return <MainComponent key={child.id} main={child} />;
            case "Article":
              return <ArticleComponent key={child.id} article={child} />;
            case "Aside":
              return <AsideComponent key={child.id} aside={child} />;
            case "HorizontalRule":
              return <HRTag key={child.id} div={child} />;
            case "LineBreak":
              return <BRTag key={child.id} div={child} />;
            case "Iframe":
              return <IframeComponent key={child.id} div={child} />;
            default:
              return <Division key={child.id} div={child} />;
          }
        })}
    </div>
  );
};

export default AsideComponent;
